import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { createFile, clearIngestJob } from '@/store/slices/content/contentSlice';
import { selectAllFolders } from '@/store/slices/folder/folderSelectors';
import { selectTags, fetchTags } from '@/store/slices/tagsSlice';
import { selectChatbots, fetchChatbotsIfNeeded } from '@/store/slices/chatbots/chatbotsSlice';
import { selectAllProfiles, fetchCopilotProfiles } from '@/store/slices/copilot-profiles/copilotProfilesSlice';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { useDropzone } from 'react-dropzone';
import { FileIcon, Upload } from 'lucide-react';
import { EnhancedTagsInput } from '../shared/EnhancedTagsInput';
import { EnhancedChatbotInput } from '../shared/EnhancedChatbotInput';
import { EnhancedCopilotInput } from '../shared/EnhancedCopilotInput';
import type { Content, ContentIngestJobState, ContentIngestEvent } from '@/types/content';
import { ContentStatus } from '@/types/content';
import { FormSection } from '../shared/FormSection';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';

interface UploadFileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatIngestStage = (stage?: string) => {
  if (!stage) return 'Processing';
  return stage
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const clamp = (value: number, min = 0, max = 100) => Math.min(max, Math.max(min, value));

const formatEventSummary = (event: ContentIngestEvent) => {
  if (event.type === 'error') {
    return `Error: ${(event as any).message ?? 'Unknown error'}`;
  }

  if (event.type === 'connected') {
    return 'Stream Connected';
  }

  const stage = typeof (event as any).stage === 'string' ? (event as any).stage : undefined;
  if (!stage) {
    return event.type;
  }

  const stageLabel = formatIngestStage(stage);
  const details: string[] = [];

  if (stage === 'extracting_text' && (event as any).filename) {
    details.push((event as any).filename as string);
  }

  if (stage === 'text_extracted' && (event as any).extracted_text_length) {
    details.push(`${(event as any).extracted_text_length} chars`);
  }

  if (stage === 'chunking_progress') {
    const pct = asNumber((event as any).progress_pct);
    const processed = asNumber((event as any).splits_processed);
    const total = asNumber((event as any).total_splits);
    if (pct !== undefined) {
      details.push(`${pct}%`);
    }
    if (processed !== undefined && total !== undefined) {
      details.push(`${processed}/${total} splits`);
    }
  }

  if (stage === 'chunking_completed' && (event as any).chunks_created) {
    details.push(`${(event as any).chunks_created} chunks`);
  }

  if (stage === 'embedding_progress') {
    const pct = asNumber((event as any).progress_pct);
    const processed = asNumber((event as any).chunks_embedded);
    const total = asNumber((event as any).total_chunks);
    if (pct !== undefined) {
      details.push(`${pct}%`);
    }
    if (processed !== undefined && total !== undefined) {
      details.push(`${processed}/${total} chunks`);
    }
  }

  if (stage === 'embedding_started' && (event as any).chunks_total) {
    details.push(`${(event as any).chunks_total} chunks`);
  }

  if (stage === 'storing_completed' && (event as any).chunks_stored) {
    details.push(`${(event as any).chunks_stored} stored`);
  }

  if (stage === 'ready' && (event as any).metadata && typeof (event as any).metadata === 'object' && (event as any).metadata.filename) {
    details.push((event as any).metadata.filename as string);
  }

  const suffix = details.length ? ` – ${details.join(' · ')}` : '';
  return `${stageLabel}${suffix}`;
};

export const UploadFileModal = ({ open, onOpenChange }: UploadFileModalProps) => {
  const dispatch = useAppDispatch();
  const folders = useAppSelector(selectAllFolders);
  const availableTags = useAppSelector(selectTags);
  const availableChatbots = useAppSelector(selectChatbots).map(chatbot => ({
    id: chatbot.id,
    name: chatbot.name
  }));
  const availableCopilots = useAppSelector(selectAllProfiles).map(copilot => ({
    id: copilot.id,
    name: copilot.name
  }));

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [linkedCopilots, setLinkedCopilots] = useState<string[]>([]);
  const [linkedChatbots, setLinkedChatbots] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [folderId, setFolderId] = useState<string | null>('all');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const activeIngestJob: ContentIngestJobState | undefined = useAppSelector(state =>
    activeJobId ? state.content.ingestJobs[activeJobId] : undefined
  );

  const jobProgress = useMemo(() => {
    if (!activeIngestJob) {
      return undefined;
    }
    const raw = asNumber(activeIngestJob.progress?.percentage);
    if (raw !== undefined) {
      return clamp(raw);
    }
    if (activeIngestJob.completed && !activeIngestJob.error) {
      return 100;
    }
    return undefined;
  }, [activeIngestJob]);

  const jobStage = useMemo(() => {
    if (!activeIngestJob) return undefined;
    return activeIngestJob.stage || activeIngestJob.status;
  }, [activeIngestJob]);

  const isJobActive = Boolean(activeIngestJob && !activeIngestJob.completed);
  const isBusy = isUploading || isJobActive;

  const recentEvents = useMemo(() => {
    if (!activeIngestJob) return [] as ContentIngestJobState['events'];
    const events = activeIngestJob.events.slice(-4);
    return events.reverse();
  }, [activeIngestJob]);

  const progressLabel = useMemo(() => {
    if (!jobStage) {
      return 'Waiting for updates...';
    }
    const stageLabel = formatIngestStage(jobStage);
    const chunking = activeIngestJob?.progress?.chunking as { processed?: number; total?: number } | undefined;
    const embedding = activeIngestJob?.progress?.embedding as { processed?: number; total?: number } | undefined;
    const raw = activeIngestJob?.progress?.raw as Record<string, unknown> | undefined;

    if (jobStage.startsWith('chunking') && chunking?.total !== undefined) {
      return `${stageLabel} (${chunking.processed ?? 0}/${chunking.total} splits)`;
    }

    if (jobStage.startsWith('embedding') && embedding?.total !== undefined) {
      return `${stageLabel} (${embedding.processed ?? 0}/${embedding.total} chunks)`;
    }

    if (jobStage === 'extracting_text' && raw?.filename) {
      return `${stageLabel} – ${raw.filename as string}`;
    }

    if (jobStage === 'ready' && raw?.metadata && typeof raw.metadata === 'object' && (raw.metadata as any).filename) {
      return `${stageLabel} – ${(raw.metadata as any).filename as string}`;
    }

    return stageLabel;
  }, [activeIngestJob, jobStage]);

  const progressPercentLabel = useMemo(() => {
    if (jobProgress === undefined) {
      return undefined;
    }
    return `${Math.round(jobProgress)}%`;
  }, [jobProgress]);

  // Fetch tags and chatbots when modal opens
  useEffect(() => {
    if (open) {
      dispatch(fetchTags({
        searchQuery: '',
        filterEntity: 'all',
        sortField: 'name',
        sortDirection: 'asc',
        page: 1,
        limit: 1000
      }));
      dispatch(fetchChatbotsIfNeeded());
      dispatch(fetchCopilotProfiles());
    }
  }, [open, dispatch]);

  // Get available folders for selection
  const availableFolders = [
    { id: 'all', name: 'All Content' },
    ...folders.filter(folder => folder.id !== 'all')
  ];

  const resetForm = useCallback(() => {
    setFile(null);
    setTitle('');
    setTags([]);
    setIsLive(false);
    setLinkedCopilots([]);
    setLinkedChatbots([]);
    setFolderId('all');
    setIsUploading(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
        console.log("file", acceptedFiles[0]);
        // Auto-populate title with file name (without extension)
        const fileName = acceptedFiles[0].name;
        const titleWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
        setTitle(titleWithoutExtension);
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    disabled: isBusy,
  });

  useEffect(() => {
    if (!activeJobId || !activeIngestJob) {
      return;
    }

    if (!activeIngestJob.completed) {
      return;
    }

    setIsUploading(false);

    if (activeIngestJob.error) {
      toast.error(activeIngestJob.error.message || 'File ingestion failed');
      dispatch(clearIngestJob({ jobId: activeIngestJob.jobId }));
      setActiveJobId(null);
      return;
    }

    const displayTitle = title || file?.name || 'Content';
    toast.success(`File "${displayTitle}" ingested successfully`);
    dispatch(clearIngestJob({ jobId: activeIngestJob.jobId }));
    setActiveJobId(null);
    resetForm();
    onOpenChange(false);
  }, [activeJobId, activeIngestJob, dispatch, file, onOpenChange, resetForm, title]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setIsUploading(true);

    const normalizedFolderId = folderId && folderId !== 'all' ? folderId : undefined;

    try {
      const contentData: Content = {
        title: title || file.name,
        content: `File uploaded: ${file.name}`,
        type: 'file' as const,
        status: 'processing' as ContentStatus,
        category: 'General',
        folderId: normalizedFolderId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        chatbots: linkedChatbots.length > 0 ? linkedChatbots : undefined,
        tags: tags.length > 0 ? tags : undefined,
        copilots: linkedCopilots.length > 0 ? linkedCopilots : undefined,
        file
      };

      const job = await dispatch(createFile(contentData)).unwrap();
      setActiveJobId(job.jobId);
      toast.success('File ingestion started. Tracking progress...');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to start ingestion. Please try again.');
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const handleClose = (force = false) => {
    if (!force && (isBusy || activeIngestJob)) {
      const jobStillRunning = activeIngestJob && !activeIngestJob.completed;
      if (jobStillRunning) {
        toast.info('Ingestion is still running. Please wait for it to complete.');
        return;
      }
    }

    if (activeJobId) {
      dispatch(clearIngestJob({ jobId: activeJobId }));
      setActiveJobId(null);
    }

    resetForm();
    onOpenChange(false);
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleClose();
    } else {
      onOpenChange(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-gray-950 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <Upload className="h-5 w-5 text-primary" />
            Upload File
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Title field */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium text-gray-700 dark:text-gray-300">Title</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              className="border-gray-200 dark:border-gray-800 focus:ring-primary/30 focus:border-primary"
              disabled={isBusy}
              required
            />
          </div>

          {/* File upload area */}
          <div className="space-y-2">
            <Label htmlFor="file" className="text-sm font-medium text-gray-700 dark:text-gray-300">File</Label>
            {!file ? (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer 
                  hover:border-primary/50 hover:bg-primary/5 transition-colors 
                  ${isDragActive ? "border-primary bg-primary/5" : "border-gray-200 dark:border-gray-700"} 
                  ${isBusy ? "pointer-events-none opacity-50" : ""}`}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-primary/60" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isDragActive
                      ? "Drop the file here"
                      : "Drag and drop a file here, or click to select"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supported formats: PDF, DOC, DOCX, TXT
                  </p>
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-4 bg-muted/20 flex items-center justify-between border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <FileIcon className="h-6 w-6 text-primary/70" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={removeFile}
                  disabled={isBusy}
                  className="text-gray-500 hover:text-destructive"
                >
                  Remove
                </Button>
              </div>
            )}
          </div>

          {(isBusy || jobProgress !== undefined || activeIngestJob) && (
            <div className="space-y-2">
              <Progress value={jobProgress ?? 0} className="h-1 bg-gray-100 dark:bg-gray-800" />
              <p className="text-xs text-center text-muted-foreground">
                {progressLabel}{progressPercentLabel ? ` · ${progressPercentLabel}` : ''}
              </p>
            </div>
          )}

          {activeIngestJob && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center justify-between text-primary">
                <span className="font-medium">Job {activeIngestJob.jobId.slice(0, 8)}</span>
                <span>{formatIngestStage(jobStage)}</span>
              </div>
              <div className="space-y-1">
                {recentEvents.length > 0 ? (
                  recentEvents.map((event, index) => {
                    const stageLabel = formatIngestStage((event as any).stage ?? event.type);
                    return (
                      <div key={`${event.type}-${index}`} className="flex items-center justify-between">
                        <span>{formatEventSummary(event)}</span>
                        <span className="text-[10px] uppercase tracking-wide text-primary/70">{stageLabel}</span>
                      </div>
                    );
                  })
                ) : (
                  <p>No updates yet...</p>
                )}
              </div>
              {activeIngestJob.error && (
                <p className="text-destructive">Error: {activeIngestJob.error.message}</p>
              )}
            </div>
          )}

          {/* Folder selection */}
          <div className="space-y-2">
            <Label htmlFor="folder" className="text-sm font-medium text-gray-700 dark:text-gray-300">Folder</Label>
            <Select
              value={folderId || 'all'}
              onValueChange={setFolderId}
              disabled={isBusy}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select folder" />
              </SelectTrigger>
              <SelectContent>
                {availableFolders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags" className="text-sm font-medium text-gray-700 dark:text-gray-300">Tags</Label>
            <EnhancedTagsInput
              tags={tags}
              setTags={setTags}
              availableTags={availableTags}
              disabled={isBusy}
              maxDisplayCount={4}
            />
          </div>

          {/* Advanced Options Section */}
          <FormSection title="Advanced Options" defaultExpanded={false}>
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="visibility" className="text-sm font-medium text-gray-700 dark:text-gray-300">Visibility</Label>
                  <p className="text-xs text-muted-foreground">Make content live after processing</p>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Switch
                        id="visibility"
                        checked={isLive}
                        onCheckedChange={setIsLive}
                        disabled={isBusy}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="left" className="bg-popover border border-border text-popover-foreground">
                      <p className="text-xs">If turned on, this content becomes available to linked profiles in real time.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedCopilots" className="text-sm font-medium text-gray-700 dark:text-gray-300">Linked Copilot Profiles</Label>
                <EnhancedCopilotInput
                  copilots={linkedCopilots}
                  setCopilots={setLinkedCopilots}
                  availableCopilots={availableCopilots}
                  disabled={isBusy}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedChatbots" className="text-sm font-medium text-gray-700 dark:text-gray-300">Linked Chatbot Profiles</Label>
                <EnhancedChatbotInput
                  chatbots={linkedChatbots}
                  setChatbots={setLinkedChatbots}
                  availableChatbots={availableChatbots}
                  disabled={isBusy}
                />
              </div>
            </div>
          </FormSection>

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => handleClose()}
              disabled={isBusy}
              className="h-9 border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isBusy || !file}
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-9"
            >
              {isBusy ? `Processing ${formatIngestStage(jobStage)}` : 'Upload File'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
